import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'localization';
import type { ArcInfo } from 'types/arc';
import type { ApiClients } from '../../../context/ApiClientsContext';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ArcActionButtonMolecule from '../../molecules/ArcActionButton/ArcActionButtonMolecule';
import TransitionCommentDialogMolecule from '../../molecules/TransitionCommentDialog/TransitionCommentDialogMolecule';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import {
  fetchArcTemplate,
  selectArcTemplate,
  selectArcTemplateLoading,
  selectArcTemplateError,
} from '@center-pwa/reducers/arcTemplatesReducer';
import { getArcHealthModifier } from '../../../utils/arc/healthUtils';
import './ArcDetailOrganism.scss';

export interface ArcDetailOrganismProps {
  arc: ArcInfo;
  apiClients: ApiClients | null;
  hasEditPermission: boolean;
  onArcUpdated?: () => void;
}

const ArcDetailOrganism: React.FC<ArcDetailOrganismProps> = ({
  arc,
  apiClients,
  hasEditPermission,
  onArcUpdated,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [commentDialogVisible, setCommentDialogVisible] = useState(false);
  const [pendingTransitionTarget, setPendingTransitionTarget] = useState<string | null>(null);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const templateSystemName = arc.templateSystemName;

  const arcTemplate = useSelector((state: RootState) => selectArcTemplate(state, templateSystemName));
  const templateLoading = useSelector((state: RootState) => selectArcTemplateLoading(state, templateSystemName));
  const templateError = useSelector((state: RootState) => selectArcTemplateError(state, templateSystemName));

  useEffect(() => {
    if (!apiClients?.schemaClient) return;
    if (!arcTemplate && !templateLoading) {
      dispatch(fetchArcTemplate({ systemName: templateSystemName, apiClient: apiClients.schemaClient }));
    }
  }, [templateSystemName, apiClients, arcTemplate, templateLoading, dispatch]);

  const stages = useMemo(() => {
    if (!arcTemplate) return [];
    return arcTemplate.definition.stages;
  }, [arcTemplate]);

  const currentStageId = arc.currentStageId;
  const healthStatus = arc.health?.status || 'on_track';

  const currentStageDef = useMemo(() => {
    return stages.find(s => s.id === currentStageId) || null;
  }, [stages, currentStageId]);

  const pendingTransitionStage = useMemo(() => {
    if (!pendingTransitionTarget) return null;
    return stages.find(s => s.id === pendingTransitionTarget) || null;
  }, [pendingTransitionTarget, stages]);

  const requireComment = useMemo(() => {
    return currentStageDef?.transition?.requireComment ?? false;
  }, [currentStageDef]);

  const handleTransition = useCallback(async (newStageId: string, comment?: string) => {
    if (!apiClients?.graphClient) return;
    try {
      setTransitionLoading(true);
      setTransitionError(null);
      const response = await apiClients.graphClient.patch(`/arcs/${arc.id}/transition`, {
        newStageId,
        comment,
      });
      if (response.status === 'success') {
        setCommentDialogVisible(false);
        setPendingTransitionTarget(null);
        onArcUpdated?.();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('arc.errors.transition_failed');
      setTransitionError(errorMessage);
    } finally {
      setTransitionLoading(false);
    }
  }, [apiClients, arc.id, onArcUpdated, t]);

  const handleCancel = useCallback(async () => {
    if (!apiClients?.graphClient) return;
    try {
      setTransitionLoading(true);
      const response = await apiClients.graphClient.post(`/arcs/${arc.id}/cancel`, {
        reason: t('arc.errors.user_cancelled'),
      });
      if (response.status === 'success') {
        onArcUpdated?.();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('arc.errors.cancel_failed');
      setTransitionError(errorMessage);
    } finally {
      setTransitionLoading(false);
    }
  }, [apiClients, arc.id, onArcUpdated, t]);

  const handleRequestComment = useCallback((targetStageId: string) => {
    setPendingTransitionTarget(targetStageId);
    setCommentDialogVisible(true);
  }, []);

  const handleCommentConfirm = useCallback((comment?: string) => {
    if (pendingTransitionTarget) {
      handleTransition(pendingTransitionTarget, comment);
    }
  }, [pendingTransitionTarget, handleTransition]);

  const handleCommentCancel = useCallback(() => {
    setCommentDialogVisible(false);
    setPendingTransitionTarget(null);
  }, []);

  if (templateLoading && !arcTemplate) {
    return (
      <div className="arc-detail">
        <div className="arc-detail__loading">
          <i className="pi pi-spin pi-spinner" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="arc-detail">
        <div className="arc-detail__error">{t('arc.errors.template_not_found')}</div>
      </div>
    );
  }

  if (!arcTemplate) {
    return null;
  }

  const templateName = arcTemplate.definition.name || arcTemplate.name || templateSystemName;

  return (
    <div className="arc-detail">
      {/* Header: arc name + health badge */}
      <div className="arc-detail__header">
        <div className="arc-detail__header-info">
          <i className="pi pi-sync arc-detail__header-icon" />
          <span className="arc-detail__name">{templateName}</span>
          {arc.isPrimary && (
            <span className="arc-detail__primary-badge">
              {t('sphereArcs.primaryBadge')}
            </span>
          )}
        </div>
        <ChipAtom
          label={t(`arc.health.${healthStatus}`)}
          className={(() => {
            const healthMod = getArcHealthModifier(healthStatus);
            return healthMod ? `arc-detail__health-chip arc-detail__health--${healthMod}` : 'arc-detail__health-chip ';
          })()}
        />
      </div>

      {/* Stage Stepper */}
      <div className="arc-detail__stepper">
        {stages.map((stage, index) => {
          const stageStatus = arc.stages.find(s => s.stageId === stage.id);
          const isActive = stage.id === currentStageId;
          const isCompleted = stageStatus?.status === 'completed';
          const isNotStarted = !stageStatus || stageStatus.status === 'not_started';

          const stepClass = [
            'arc-detail__step',
            isActive ? 'arc-detail__step--active' : '',
            isCompleted ? 'arc-detail__step--completed' : '',
            isNotStarted ? 'arc-detail__step--not-started' : '',
          ].filter(Boolean).join(' ');

          return (
            <React.Fragment key={stage.id}>
              <div className={stepClass}>
                <div className="arc-detail__step-indicator">
                  {isCompleted ? (
                    <i className="pi pi-check" />
                  ) : (
                    <span className="arc-detail__step-number">{index + 1}</span>
                  )}
                </div>
                <span className="arc-detail__step-label">
                  {t(stage.localizationKey, stage.fallbackName)}
                </span>
              </div>
              {index < stages.length - 1 && (
                <div className={`arc-detail__step-connector ${isCompleted ? 'arc-detail__step-connector--completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Current stage info */}
      {currentStageDef && (
        <div className="arc-detail__current-stage">
          <span className="arc-detail__current-stage-label">
            {t('arc.ui.current_stage_label')}:
          </span>
          <span className="arc-detail__current-stage-name">
            {t(currentStageDef.localizationKey, currentStageDef.fallbackName)}
          </span>
          {arc.stageEnteredAt && (
            <span className="arc-detail__stage-date">
              {t('arc.dates.started_at')}: {new Date(arc.stageEnteredAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {/* Transition error */}
      {transitionError && (
        <div className="arc-detail__error">{transitionError}</div>
      )}

      {/* Action buttons */}
      {hasEditPermission && currentStageDef && (
        <div className="arc-detail__actions">
          <ArcActionButtonMolecule
            arcTemplate={arcTemplate}
            currentStageId={currentStageId}
            onTransition={handleTransition}
            onCancel={handleCancel}
            onRequestComment={handleRequestComment}
            disabled={transitionLoading}
          />
        </div>
      )}

      {/* Transition comment dialog */}
      <TransitionCommentDialogMolecule
        visible={commentDialogVisible}
        targetStageName={
          pendingTransitionStage
            ? t(pendingTransitionStage.localizationKey, pendingTransitionStage.fallbackName)
            : undefined
        }
        required={requireComment}
        onConfirm={handleCommentConfirm}
        onCancel={handleCommentCancel}
      />
    </div>
  );
};

export default ArcDetailOrganism;
